import _                                                                                                                                           from 'underscore';
import React, { MutableRefObject, useContext }                                                                                                     from 'react';
import { DynamicComponentsContext }                                                                                                                from '../views/contexts';
import { AssumptionIdOrFieldName, PlanningAreaParamsSection, ProjectManagementParamsSettingsSection, RouterLocationStates, SemanticUIColor, Uuid } from '../types';
import { useHistory, useLocation }                                                                                                                 from 'react-router';

import { RouterUtility }  from '../util';
import { ProjectContext } from '../views/pages/projects/ProjectIndexPage';
import { BasicDialog, BasicDialogProps } from '../views/components/modals/BasicDialog';
import { BasicDialogContainer }          from '../views/components/modals/BasicDialogContainer';
import { DangerMessage }                 from '../views/components/util/messaging/DangerMessage';
import { runIfFunction }                 from '../util';

interface ProjectAssumptionsHook {

	/* Asks the user if they are sure they want to proceed when clicking an inquiry question */
	confirmEditInquiryAssumption( intakeQuestion: AssumptionIdOrFieldName, onConfirm ?: ( ...data: any ) => void ): void,

	/* Asks the user if they are sure they want to proceed when updating an answer */
	confirmAssumptionUpdate( onConfirm: () => void, onCancel: () => void, dialogProps?: Partial<BasicDialogProps> ): void

	/* Sends user to a route with specified intake question information */
	sendUserToAssumption( routePath: string, id: Uuid ): void,

	/* Sends user to a category survey with specified intake question information */
	sendUserToSurveyAssumption( id: Uuid, categoryId: Uuid ): void,

	/* Sends user to inquiry survey with specified intake question information */
	sendUserToInquiryAssumption( id: Uuid ): void,

	/* Sends user to project settings with 'from' route information
	* Used for clarifying dates like custom start date, etc. */
	sendUserToProjectSettings( section: ProjectManagementParamsSettingsSection, isEditing: boolean ): void,
}

const ProjectAssumptionsHookRef: MutableRefObject<ProjectAssumptionsHook> = React.createRef();

export const useProjectAssumptions = (): ProjectAssumptionsHook => {

	let history                        = useHistory(),
	    location                       = useLocation<RouterLocationStates.Survey>(),
	    { toasts: [ , toastActions ] } = useContext( DynamicComponentsContext ),
	    { modal: [ , modalActions ] }  = useContext( DynamicComponentsContext ),
	    { projectId }                  = useContext( ProjectContext );

	const sendUserToAssumption = ( routePath: string, id: AssumptionIdOrFieldName ) => {

		history.push( routePath, {
			questionId: id,
			from:       location.pathname,
		} );
	}

	const sendUserToSurveyAssumption: ProjectAssumptionsHook['sendUserToSurveyAssumption'] = ( id, categoryId ) => {

		let routePath = RouterUtility.getRoute( 'PROJECT.SURVEYS.CATEGORY', { projectId, categoryId } );

		sendUserToAssumption( routePath, id );
	}

	const sendUserToInquiryAssumption: ProjectAssumptionsHook['sendUserToInquiryAssumption'] = ( id ) => {

		let routePath = RouterUtility.getRoute( 'PROJECT.SURVEYS.INQUIRY', { projectId } );

		sendUserToAssumption( routePath, id );
	}

	const sendUserToProjectSettings: ProjectAssumptionsHook['sendUserToProjectSettings'] = ( section = null, isEditing = false ) => {

		let sectionHash: Record<ProjectManagementParamsSettingsSection, string> = {
			[ ProjectManagementParamsSettingsSection.DETAILS ]:  PlanningAreaParamsSection.PROJECT_DETAILS,
			[ ProjectManagementParamsSettingsSection.TIMELINE ]: PlanningAreaParamsSection.PROJECT_KEY_MILESTONES,
			[ ProjectManagementParamsSettingsSection.TEAM ]:     null,
		}

		let routePath = RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId, section: sectionHash[ section ] ?? PlanningAreaParamsSection.PROJECT_DETAILS } );

		history.push( routePath, {
			from: location.pathname,
			isEditing,
		} );
	}

	const confirmEditInquiryAssumption: ProjectAssumptionsHook['confirmEditInquiryAssumption'] = ( intakeQuestion, onConfirm = null ) => {

		modalActions.show( (

			<BasicDialog
				operationIsDangerous={ true }
				submitLabel={ 'Proceed' }
				className={ 'no-padding' }
				onSubmit={ () => {

					modalActions.toggle();

					if( _.isFunction( onConfirm ) ){

						onConfirm( intakeQuestion )

					} else {

						sendUserToInquiryAssumption( intakeQuestion );
					}

				} }>

				<BasicDialogContainer titleColor={ SemanticUIColor.RED } title={ 'Before You Go...' }>

					<DangerMessage>This assumption was answered during your initial inquiry.</DangerMessage>
					<p>Altering your initial inquiry information can have large impacts on your assumptions, objectives, and activities your current commericalization map is associated with.</p>
					<p><strong>If you are sure your team is ready for potential updates and shifts including timing, click proceed.</strong></p>

				</BasicDialogContainer>

			</BasicDialog>

		), { size: 'tiny' } );
	}

	// Confirmation dialog when user changes survey answer
	const confirmAssumptionUpdate: ProjectAssumptionsHook['confirmAssumptionUpdate'] = ( onConfirm = null, onCancel = null, dialogProps = {} ) => {

		modalActions.show( (
			<BasicDialog
				operationIsDangerous={ false }
				submitLabel={ 'Update' }
				cancelLabel={ 'Cancel' }
				className={ 'no-padding' }
				onSubmit={ () => {

					modalActions.toggle();

					runIfFunction( onConfirm );

				} }
				onClose={ () => runIfFunction( onCancel ) }
				{ ...dialogProps }>

				<BasicDialogContainer titleColor={ SemanticUIColor.ORANGE } title={ 'Assumption Update Notice' }>

					<p>Updating this assumption can lead to additions, removals, and shifts in your project's timing and budget.</p>
					<p><strong>Are you sure you want to proceed?</strong></p>

				</BasicDialogContainer>

			</BasicDialog>

		), { size: 'tiny' } );
	}

	ProjectAssumptionsHookRef.current = {
		confirmEditInquiryAssumption,
		confirmAssumptionUpdate,
		sendUserToAssumption,
		sendUserToSurveyAssumption,
		sendUserToInquiryAssumption,
		sendUserToProjectSettings,
	};

	return ( ProjectAssumptionsHookRef.current )
};
